import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import UserIcon from "apollo-react-icons/User";
import Avatar from "apollo-react/components/Avatar";
import NavigationBar from "apollo-react/components/NavigationBar";
import Typography from "apollo-react/components/Typography";
import { useAuthContext } from '../contexts/AuthProvider';
import { appConfig, isInternalDomain } from '../config/appConfig';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthContext();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Detect screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load MyAccount widget script (but don't auto-render it)
  useEffect(() => {
    // Expose React and ReactDOM globally for the MyAccount widget
    (window as any).React = React;
    (window as any).ReactDOM = ReactDOM;

    // Dynamically load the MyAccount script
    const loadMyAccountScript = () => {
      if (!appConfig.APP_MYACCOUNT_ENDPOINT) {
        return;
      }

      const scriptId = 'my-account-script';
      const existingScript = document.getElementById(scriptId);
      
      if (existingScript) {
        return; // Script already loaded
      }

      const script = document.createElement('script');
      script.id = scriptId;
      script.src = `${appConfig.APP_MYACCOUNT_ENDPOINT}/js/my_account.js`;
      script.crossOrigin = 'anonymous';
      script.type = 'text/javascript';
      
      script.onload = () => {
        fetch(script.src, { mode: 'cors', credentials: 'omit' })
          .then(response => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.text();
          })
          .then(scriptContent => {
            const wrapperScript = document.createElement('script');
            wrapperScript.type = 'text/javascript';
            wrapperScript.textContent = `
              (function() {
                ${scriptContent}
                if (typeof MyAccount !== 'undefined') {
                  window.MyAccount = MyAccount;
                }
              })();
            `;
            document.body.appendChild(wrapperScript);
          })
          .catch(() => {
            console.warn('Failed to load MyAccount widget');
          });
      };
      document.body.appendChild(script);
    };

    if (isAuthenticated && user?.profile) {
      loadMyAccountScript();
    }
  }, [isAuthenticated, user]);

  // Helper function to create full name
  const createFullName = (profile: any): string => {
    if (profile?.name) {
      if (typeof profile.name === 'string' && profile.name.trim()) {
        return profile.name.trim();
      } else if (Array.isArray(profile.name) && profile.name.length > 0) {
        const nameFromArray = profile.name.join('').trim();
        if (nameFromArray) return nameFromArray;
      }
    }
    
    const firstName = (profile?.given_name && typeof profile.given_name === 'string') ? profile.given_name.trim() : '';
    const lastName = (profile?.family_name && typeof profile.family_name === 'string') ? profile.family_name.trim() : '';
    
    if (firstName && lastName) return `${firstName} ${lastName}`;
    if (firstName) return firstName;
    if (lastName) return lastName;
    
    if (profile?.preferred_username && typeof profile.preferred_username === 'string' && profile.preferred_username.trim()) {
      return profile.preferred_username.trim();
    }
    
    return "Unknown User";
  };

  const getUserInitials = () => {
    const name = user?.profile ? createFullName(user.profile) : "Unknown User";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const getProfileName = (): string => {
    if (user?.profile) {
      return createFullName(user.profile);
    }
    return "Unknown User";
  };

  const getProfileTitle = (): string => {
    const name = getProfileName();
    if (name && name !== "Unknown User") {
      return name.replace(/\b\w/g, (c) => c.toUpperCase());
    }
    return "Unknown User";
  };

  const getProfileEmail = (): string => {
    const email = user?.profile?.email;
    if (typeof email === 'string') {
      return email;
    }
    return "No email provided";
  };

  const getAvatarAlt = (): string => {
    return getProfileName();
  };

  // Check if user should have clickable profile (not in internal domains from appConfig)
  const isProfileClickable = () => {
    const email = user?.profile?.email;
    const userEmail = typeof email === 'string' ? email.toLowerCase() : '';
    
    if (!userEmail) return false;
    
    // Extract domain from email (everything after @)
    const emailDomain = userEmail.split('@')[1];
    if (!emailDomain) return false;
    
    // User can click profile if their domain is NOT in the internal domains list
    return !isInternalDomain(emailDomain);
  };

  // Handler for Account Settings - Opens MyAccount widget URL in new tab
  const handleAccountSettings = () => {
    if (!user?.profile) return;

    const userEmail = getProfileEmail();
    const loginHint = encodeURIComponent(userEmail);
    // Get claims from user profile
    const inetIdpClaim = (user.profile as any)?.inet_idp || (user.profile as any)?.['inet_idp'] || '';
    const inetIdpBoolean = String(inetIdpClaim).toLowerCase() === 'true';

    // Build MyAccount URL
    /* const myAccountUrl = `${appConfig.APP_MYACCOUNT_ENDPOINT}?inet_idp=${inetIdpBoolean}`;
    console.log('Opening MyAccount URL in new tab:', myAccountUrl); */
    
    // Open in new tab
    /* window.open(myAccountUrl, '_blank'); */
    // Build MyAccount redirect URI
    const redirectUri = encodeURIComponent(`${appConfig.APP_MYACCOUNT_ENDPOINT}`);

    // Build B2C OAuth URL for profile edit
    const b2cUrl = `${appConfig.B2C_LOGIN_URL}?` +
      `p=${appConfig.B2C_1A_PARAMS}&` +
      `client_id=${appConfig.SDA_CLIENT_ID_B2C}&` +
      `nonce=DefaultNonce&` +
      `redirect_uri=${redirectUri}&` +
      `scope=openid&` +
      `response_type=id_token&` +
      `login_hint=${loginHint}&` +
      `inet_idp=${inetIdpBoolean}`;

    console.log('Opening B2C profile edit URL in new tab:', b2cUrl);
    
    // Open in new tab
    window.open(b2cUrl, '_blank');
  };

  const profileMenuProps = {
    name: getProfileName(),
    title: getProfileTitle(),
    email: getProfileEmail(),
    logoutText: "", // Empty string to hide logout button
    icon: (
      <Avatar size="small" alt={getAvatarAlt()} stateless>
        {getUserInitials()}
      </Avatar>
    ),
    logoutButtonProps: {
      style: { display: 'none' }, // Hide the logout button
    },
    extraContent: (
      <div style={{ 
        padding: '8px 16px 16px 16px',
        textAlign: 'center'
      }}>
        <Typography
          component="p"
          variant="caption"
          style={{
            color: "#999",
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: '1.5'
          }}
        >
          {appConfig.APP_VERSION}
        </Typography>
      </div>
    ),
    menuItems: isProfileClickable() ? [
      {
        text: "Account settings",
        icon: UserIcon,
        onClick: handleAccountSettings,
      },
    ] : [],
  };

  return (
    <NavigationBar
      LogoComponent={() => (
        <Typography
          style={{
            color: "white",
            lineHeight: "56px",
            cursor: "pointer",
            zIndex: 2,
            whiteSpace: "nowrap",
          }}
          onClick={() => navigate("/")}
        >
          <span style={{ fontWeight: 400, fontSize: 16, letterSpacing: '0.5px' }}>
            IQVIA
          </span>
          {" "}
          <span style={{ fontWeight: 600, fontSize: 16 }}>
            Connected Devices Site Gateway
          </span>
        </Typography>
      )}
      profileMenuProps={profileMenuProps}
      maxWidth="100%"
      waveVariant="both"
      variant="brand"
      truncate={true}
      groupingButtonLabel="Menu"
      style={{ pointerEvents: "auto" }}
    />
  );
};

export default Header;
